import { createSelector } from 'reselect';
import { KEY, CartState } from './types';
import { GlobalState } from 'store/types';

const getCartState = (state: GlobalState) => state[KEY] as CartState;

const selectLoading = createSelector(getCartState, (cart) => cart.loading);

const selectError = createSelector(getCartState, (cart) => cart.error);

const selectPurchaseError = createSelector(getCartState, (cart) => cart.purchaseError);

const selectCart = createSelector(getCartState, (cart) => cart.cart);

/**
 * The cart does not include the gift voucher amount,
 * so this will subtract the gift voucher amount from
 * the cart total to return the total amount to charge.
 */
const selectCartTotalToCharge = createSelector(getCartState, (state) => {
  const { cart, giftVoucher } = state;
  let cartTotal = cart && cart.total ? cart.total : 0;
  const giftVoucherAmount = giftVoucher && giftVoucher.amount ? giftVoucher.amount : 0;
  cartTotal = cartTotal > giftVoucherAmount ? cartTotal - giftVoucherAmount : 0;

  return cartTotal;
});

const selectPurchased = createSelector(getCartState, (cart) => cart.purchased);

const selectCheckoutCart = createSelector(getCartState, (state) => state.checkoutCart);

const selectReserving = createSelector(getCartState, (cart) => cart.reserving);

const selectPromoCode = createSelector(getCartState, (cart) => cart.promoCode);

const selectPromoCodeString = createSelector(getCartState, (cart) => cart.promoCode.promoCode);

const selectGiftVoucher = createSelector(getCartState, (cart) => cart.giftVoucher);

const selectGiftVoucherCode = createSelector(getCartState, (cart) => cart.giftVoucher.voucherCode);

const selectGiftVoucherAmountToUse = createSelector(getCartState, (cart) => {
  if (cart.cart && cart.cart.total && cart.giftVoucher.amount) {
    return cart.cart.total > cart.giftVoucher.amount ? cart.giftVoucher.amount : cart.cart.total;
  }
  return undefined;
});

const selectDonation = createSelector(getCartState, (cart) => cart.donation);

/**
 * Return the membership product in the cart if there is one.
 */
const selectMembershipProductInCart = createSelector(getCartState, (cart) => {
  return (
    cart.cart &&
    cart.cart.products &&
    cart.cart.products.find((product) => {
      return product.type ? product.type === 'membership' : undefined;
    })
  );
});

/**
 * Return the donation product in the cart if there is one.
 */
const selectDonationProductInCart = createSelector(getCartState, (cart) => {
  return (
    cart.cart &&
    cart.cart.products &&
    cart.cart.products.find((product) => {
      return product.type ? product.type === 'donation' : undefined;
    })
  );
});

/**
 * Return a list of id(s) for all items in cart products & subProducts.
 */
const selectCartItemIds = createSelector(getCartState, (cart) => {
  const itemIds: string[] = [];

  if (cart.cart && cart.cart.products && cart.cart.products.length) {
    cart.cart.products.forEach((product) => {
      if (product.items) {
        itemIds.push(...product.items.map((item) => item.id));
      } else if (product.subProducts) {
        product.subProducts.forEach((subProduct) => {
          if (subProduct.items) {
            itemIds.push(...subProduct.items.map((item) => item.id));
          }
        });
      }
    });
  }

  return itemIds;
});

export const selectors = {
  selectLoading,
  selectError,
  selectPurchaseError,
  selectCart,
  selectCartTotalToCharge,
  selectPurchased,
  selectCheckoutCart,
  selectReserving,
  selectPromoCode,
  selectPromoCodeString,
  selectGiftVoucher,
  selectGiftVoucherCode,
  selectGiftVoucherAmountToUse,
  selectDonation,
  selectMembershipProductInCart,
  selectDonationProductInCart,
  selectCartItemIds
};
